import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Modal from "@material-ui/core/Modal";
import { ButtonStyled } from "components/buttons/buttons.styled";
import { LabelStyled, TextArea } from "components/form/inputs/inputs.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { useApplicationStore } from "web-apps/company/stores/applicationStore/applicationStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useAlertStore } from "stores/alertStore/alertStore";
import {
  getWorkerImg,
  getWorkerVideo,
} from "services/firebase/firebase.service";
import {
  ApplicationDto,
  ApplicationResolution,
  ApplicationStatus,
  ApplicationUserSkillDto,
  ApplicationWorkerEndorsementDto,
  ApplicationWorkerStat,
} from "model/Application";
import { CompanyFavoriteDto } from "model/Favorite";
import { GigType } from "model/Gig";
import { formatWorkerDateOfBirth } from "web-apps/company/utils/utils";
import { toApplicationStatusUpdate } from "web-apps/company/components/applications/applicationStatus.logic";
import { WorkerDocuments } from "web-apps/company/components/workerDocument/workerDocument.component";
import {
  VideoPresentation,
  DividerLine,
  AboutDiv,
  GreenCell,
  Cell,
  SkillsCell,
  RowWrapper,
  ProfileDetailsContainer,
  ApplicantGrid,
  ProfileDiv,
  CompanyImage,
  ModalBody,
  ButtonContainer,
  ImageContainer,
  BackCustomLink,
  VideoContainer,
  VideoWrapper,
  Column,
  SkillCell,
  SkillsContainer,
  StatsContainer,
} from "./applicantDetails.styled";
import { WorkerEndorementModal } from "./workerEndorsementModal";

type Props = {
  readonly applicant: ApplicationDto;
};

enum Response {
  REJECT,
  ACCEPT,
}

export const ApplicantDetails: React.FC<Props> = ({ applicant }) => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [, applicationActions] = useApplicationStore();
  const applicationActionsRef = useRef(applicationActions);
  applicationActionsRef.current = applicationActions;
  const [companyState, companyActions] = useCompanyStore();
  const [workerProfileImage, setWorkerProfileImage] = useState("");
  const [workerVideo, setWorkerVideo] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [favoriteModalOpen, setFavoriteModalOpen] = useState(false);
  const [favorited, setFavorited] = useState<CompanyFavoriteDto | null>(null);
  const [respondApplication, setRespondApplication] = useState<Response>();
  const [declineReason, setDeclineReason] = useState("");
  const [createWorkerEndorsementSkill, setCreateWorkerEndorsementSkill] =
    useState<ApplicationUserSkillDto | null>(null);
  const [workerEndorsements, setWorkerEndorsements] = useState<
    ApplicationWorkerEndorsementDto[]
  >([]);
  const [skillAlreadyEndorsed, setSkillAlreadyEndorsed] =
    useState<boolean>(false);
  const [workerEndorsementModalOpen, setWorkerEndorsementModalOpen] =
    useState(false);
  const history = useHistory();
  const isPublicShift = applicant.gig.type === GigType.PUBLIC;
  useEffect(() => {
    const fetchWorkerEndorsements = async (
      companyId: number,
      workerId: number,
    ): Promise<void> => {
      const res =
        await applicationActionsRef.current.getApplicationWorkerEndorsements(
          companyId,
          workerId,
        );
      setWorkerEndorsements(res.data);
    };

    const companyId = companyState.company?.id;
    if (companyId && applicant.worker.id) {
      fetchWorkerEndorsements(companyId, applicant.worker.id).catch(() =>
        setWorkerEndorsements([]),
      );
    }
  }, [applicant.worker.id, companyState.company?.id]);

  useEffect(() => {
    const matching = companyState.favorites.find((worker) => {
      return worker.workerId === applicant.worker.id;
    });
    if (matching) {
      setFavorited(matching);
    } else {
      setFavorited(null);
    }
  }, [applicant.worker.id, companyState.favorites]);

  const updateApplicationStatus = (
    status: ApplicationStatus.OFFERED | ApplicationStatus.CLOSED,
  ): void => {
    if (companyState.company) {
      const statusUpdate = toApplicationStatusUpdate(status);
      const data = {
        ...applicant,
        ...statusUpdate,
        ...(statusUpdate.resolution === ApplicationResolution.REJECTED && {
          declineReason,
        }),
      };
      applicationActions
        .updateApplicationStatus(companyState.company.id, applicant.id, data)
        .then(() => {
          setModalOpen(false);
          setRespondApplication(undefined);
          setDeclineReason("");
          if (statusUpdate.resolution === ApplicationResolution.ACCEPTED) {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "checkmark",
                title: t("ApplicationsCompany.Assigned"),
                message: t("ApplicationsCompany.ApplicantHasBeenAssignedGig"),
              },
            });
          } else {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "checkmark",
                title: t("ApplicationsCompany.Rejected"),
                message: t("ApplicationsCompany.ApplicationHasBeenRejected"),
              },
            });
          }
        })
        .catch(() => {
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "alert",
              title: t("Alerts.OhNo"),
              message: t("ApplicationsCompany.ApplicationCouldNotBeUpdated"),
            },
          });
        });
    }
  };

  const handleAcceptApplicant = () => {
    setDeclineReason("");
    setRespondApplication(Response.ACCEPT);
    setModalOpen(true);
  };

  const handleRejectApplicant = () => {
    setDeclineReason("");
    setRespondApplication(Response.REJECT);
    setModalOpen(true);
  };

  const closeResponseModal = (): void => {
    setModalOpen(false);
    setRespondApplication(undefined);
    setDeclineReason("");
  };

  const removeFromFavorites = () => {
    if (favorited && companyState.company) {
      companyActions
        .removeCompanyFavorite(companyState.company?.id, favorited.workerId)
        .then(() => setFavoriteModalOpen(false))
        .catch(() => {
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "alert",
              title: t("Alerts.OhNo"),
              message: t("General.ErrorOccurred"),
            },
          });
        });
    }
  };

  const addToFavorites = () => {
    if (companyState.company) {
      companyActions
        .addCompanyFavorite(companyState.company?.id, applicant.worker.id)
        .then()
        .catch((error: unknown) => {
          if (
            axios.isAxiosError(error) &&
            error.response?.data === "Worker does not have a valid SSN"
          ) {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "alert",
                title: t("Alerts.OhNo"),
                message: t("ApplicationsCompany.WorkerDoesNotHaveAValidSSN"),
              },
            });
          }
        });
    }
  };

  const renderFavoriteModalBody = () => {
    return (
      <>
        <Text
          align="center"
          fontSize={FontSize.H4}
          fontFamily={FontFamily.MontserratSemiBold}
        >
          {t("CompanyFavorite.AreYouSureRemoveFavorite")}
        </Text>
        <ButtonContainer>
          <ButtonStyled
            backgroundColor={Color.Disabled}
            color={Color.SeaBlue500}
            onClick={() => {
              setFavoriteModalOpen(false);
            }}
          >
            {t("General.Abort")}
          </ButtonStyled>
          <ButtonStyled
            backgroundColor={Color.Destructive}
            onClick={() => removeFromFavorites()}
          >
            {t("CompanyFavorite.Remove")}
          </ButtonStyled>
        </ButtonContainer>
      </>
    );
  };

  const createEndorsement = (skill: string) => {
    const companyEndorsement = workerEndorsements.find(
      (e) => e.companyId === companyState?.company?.id && e.skill === skill,
    );

    if (companyEndorsement) {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("Alerts.SomethingWentWrong"),
        },
      });
    } else {
      if (skill && applicant.worker.id && companyState?.company?.id) {
        applicationActions
          .createApplicationWorkerEndorsement(
            companyState?.company?.id,
            applicant.worker.id,
            skill,
          )
          .then((res) => {
            if (res.data?.id) {
              setWorkerEndorsements([...workerEndorsements, res.data]);
              alertDispatch({
                type: "SHOW_ALERT",
                payload: {
                  icon: "checkmark",
                  title: t("ApplicationsCompany.Endorsed"),
                  message: t(
                    "ApplicationsCompany.CreateEndorsementSuccessfully",
                  ),
                },
              });
            } else {
              alertDispatch({
                type: "SHOW_ALERT",
                payload: {
                  icon: "alert",
                  title: t("Alerts.OhNo"),
                  message: t("Alerts.SomethingWentWrong"),
                },
              });
            }
            setWorkerEndorsementModalOpen(false);
          });
      }
    }
  };
  const deleteEndorsement = (skill: string) => {
    const endorsementToRemove = workerEndorsements.find(
      (e) =>
        e.companyId === companyState?.company?.id &&
        e.skill.toLowerCase() === skill.toLowerCase(),
    );

    if (endorsementToRemove) {
      applicationActions
        .deleteApplicationWorkerEndorsement(
          endorsementToRemove.companyId,
          endorsementToRemove.workerId,
          endorsementToRemove.id,
        )
        .then((res) => {
          if (res.status === 200) {
            const filteredEndorsements = workerEndorsements.filter(
              (e) => e.id !== endorsementToRemove?.id,
            );
            setWorkerEndorsements(filteredEndorsements);

            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "checkmark",
                title: t("General.Deleted"),
                message: t("ApplicationsCompany.DeleteEndorsementSuccessfully"),
              },
            });
          } else {
            alertDispatch({
              type: "SHOW_ALERT",
              payload: {
                icon: "alert",
                title: t("Alerts.OhNo"),
                message: t("Alerts.SomethingWentWrong"),
              },
            });
          }
          setWorkerEndorsementModalOpen(false);
        });
    } else {
      setWorkerEndorsementModalOpen(false);
    }
  };
  const renderApplicationModalBody = () => {
    if (respondApplication === Response.ACCEPT) {
      return (
        <>
          <Text
            align="center"
            fontSize={FontSize.H4}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("ApplicationsCompany.AssignApplicant", {
              name: `${applicant.worker.firstName} ${applicant.worker.lastName}`,
            })}
          </Text>
          <ButtonContainer>
            <ButtonStyled
              backgroundColor={Color.Disabled}
              color={Color.SeaBlue500}
              onClick={() => {
                closeResponseModal();
              }}
            >
              {t("GigsCompany.Discard")}
            </ButtonStyled>
            <ButtonStyled
              backgroundColor={Color.BurntSienna}
              onClick={() => updateApplicationStatus(ApplicationStatus.OFFERED)}
            >
              {t("ApplicationsCompany.Assign")}
            </ButtonStyled>
          </ButtonContainer>
        </>
      );
    } else if (respondApplication === Response.REJECT) {
      return (
        <>
          <Text
            align="center"
            fontSize={FontSize.H4}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {t("GigsCompany.YouAreAboutToRejectApplication", {
              user: applicant.worker.firstName,
            })}
          </Text>
          <LabelStyled>{t("GigsCompany.NoteToApplicant")}</LabelStyled>
          <TextArea
            placeholder={t("GigsCompany.SendANote")}
            rows={5}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
          <ButtonContainer>
            <ButtonStyled
              backgroundColor={Color.Disabled}
              color={Color.SeaBlue500}
              onClick={() => {
                closeResponseModal();
              }}
            >
              {t("GigsCompany.Discard")}
            </ButtonStyled>
            <ButtonStyled
              backgroundColor={Color.Destructive}
              onClick={() => updateApplicationStatus(ApplicationStatus.CLOSED)}
            >
              {t("GigsCompany.Reject")}
            </ButtonStyled>
          </ButtonContainer>
        </>
      );
    }
  };

  useEffect(() => {
    let isCurrent = true;
    const workerImageId =
      applicant.worker.externalId?.trim() ||
      applicant.worker.firebaseId?.trim() ||
      null;
    const workerVideoId = applicant.worker.firebaseId?.trim() || null;
    setWorkerProfileImage("");
    setWorkerVideo(null);
    if (workerImageId) {
      getWorkerImg(workerImageId)
        .then((res) => {
          if (isCurrent) setWorkerProfileImage(res ?? "");
        })
        .catch(() => {
          if (isCurrent) setWorkerProfileImage("");
        });
    }
    if (workerVideoId) {
      getWorkerVideo(workerVideoId)
        .then((res) => {
          if (isCurrent) setWorkerVideo(res);
        })
        .catch(() => {
          if (isCurrent) setWorkerVideo(null);
        });
    }
    return () => {
      isCurrent = false;
    };
  }, [applicant.worker.externalId, applicant.worker.firebaseId]);

  return (
    <>
      <BackCustomLink
        color={Color.Destructive}
        onClick={() => history.goBack()}
      >
        {t("General.Back")}
      </BackCustomLink>

      <ApplicantGrid>
        <ProfileDiv>
          <ImageContainer>
            {workerProfileImage ? (
              <CompanyImage src={workerProfileImage} />
            ) : (
              <CustomIcon
                key={1}
                className="icon"
                square
                width="250px"
                name="person"
                size="190px"
                color={Color.SeaBlue500}
                backgroundColor={Color.SeaBlue300}
                padding="10px"
              />
            )}

            {favorited ? (
              <CustomIcon
                key={2}
                className="icon"
                name="favorites-active"
                size="48px"
                color={Color.White}
                backgroundColor={Color.BurntSienna}
                padding="10px"
                onClick={() => setFavoriteModalOpen(true)}
              />
            ) : (
              <CustomIcon
                key={3}
                className="icon"
                name="favorites-inactive"
                size="48px"
                color={Color.White}
                backgroundColor={Color.BurntSienna}
                padding="10px"
                onClick={addToFavorites}
              />
            )}
          </ImageContainer>
          <Text
            fontSize={FontSize.H4}
            fontFamily={FontFamily.MontserratSemiBold}
          >
            {applicant.worker.firstName} {applicant.worker.lastName}
          </Text>
          {applicant.worker.dateOfBirth?.trim() && (
            <Text
              fontSize={FontSize.H4}
              fontFamily={FontFamily.MontserratSemiBold}
            >
              {formatWorkerDateOfBirth(applicant.worker.dateOfBirth)}
            </Text>
          )}
          <StatsContainer>
            {applicant.worker.stats &&
              applicant.worker.stats.some(
                (stat: ApplicationWorkerStat) =>
                  stat.stat === "AmountOfHoursWorked",
              ) && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <CustomIcon
                      name="clock"
                      color={Color.BurntSienna}
                      size="24px"
                      style={{ marginRight: "4px" }}
                    />
                    <Text
                      color={Color.MidnightBlue}
                      fontFamily={FontFamily.MontserratBold}
                      fontSize={FontSize.Large}
                    >
                      {applicant.worker.stats &&
                      applicant.worker.stats.some(
                        (stat: ApplicationWorkerStat) =>
                          stat.stat === "AmountOfHoursWorked",
                      )
                        ? (applicant.worker.stats
                            .find(
                              (stat: ApplicationWorkerStat) =>
                                stat.stat === "AmountOfHoursWorked",
                            )
                            ?.value.toFixed(0) ?? 0)
                        : 0}
                    </Text>
                  </div>
                  <Text color={Color.SeaBlue500}>
                    {t("ProfileWorker.HoursWorked")}
                  </Text>
                </div>
              )}
            {applicant.worker.stats &&
            applicant.worker.stats.some(
              (stat: ApplicationWorkerStat) => stat.stat === "Rating",
            ) ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <CustomIcon
                    name="star"
                    color={Color.BurntSienna}
                    size="20px"
                    style={{ marginRight: "4px" }}
                  />
                  <Text
                    color={Color.MidnightBlue}
                    fontFamily={FontFamily.MontserratBold}
                    fontSize={FontSize.Large}
                  >
                    {applicant.worker.stats &&
                    applicant.worker.stats.some(
                      (stat: ApplicationWorkerStat) => stat.stat === "Rating",
                    )
                      ? (applicant.worker.stats
                          .find(
                            (stat: ApplicationWorkerStat) =>
                              stat.stat === "Rating",
                          )
                          ?.value.toFixed(1) ?? 0)
                      : 0}
                    /5
                  </Text>
                  <Text
                    color={Color.SeaBlue500}
                    fontFamily={FontFamily.MontserratRegular}
                    fontSize={FontSize.Standard}
                    style={{ marginLeft: "4px" }}
                  >
                    {applicant.worker.stats &&
                    applicant.worker.stats.some(
                      (stat: ApplicationWorkerStat) =>
                        stat.stat === "AmountOfRatings",
                    )
                      ? `(${
                          applicant.worker.stats
                            .find(
                              (stat: ApplicationWorkerStat) =>
                                stat.stat === "AmountOfRatings",
                            )
                            ?.value.toFixed(0) ?? 0
                        })`
                      : "(" + 0 + ")"}
                  </Text>
                </div>
                <Text color={Color.SeaBlue500}>
                  {t("ProfileWorker.Rating")}
                </Text>
              </div>
            ) : (
              <Text color={Color.SeaBlue500}>
                {t("ProfileWorker.NoRatingsYet")}
              </Text>
            )}
          </StatsContainer>

          <ProfileDetailsContainer>
            <LabelStyled>{t("ProfileWorker.Contact")}</LabelStyled>
            <Text
              style={{ wordBreak: "break-all" }}
              fontFamily={FontFamily.MontserratSemiBold}
            >
              <a
                href={
                  applicant.worker.email && "mailto:" + applicant.worker.email
                }
                style={{ color: Color.BurntSienna }}
              >
                {applicant.worker.email || ""}
              </a>
            </Text>

            <Text
              style={{ wordBreak: "break-all" }}
              fontFamily={FontFamily.MontserratSemiBold}
            >
              <a
                style={{ color: Color.BurntSienna }}
                href={`tel:+${applicant.worker.phoneNumber}`}
              >
                {`+${applicant.worker.phoneNumber}`}
              </a>
            </Text>
          </ProfileDetailsContainer>
          <ProfileDetailsContainer>
            <div style={{ height: 300 }}>
              <LabelStyled>{t("ProfileWorker.Bio")}</LabelStyled>
              <Text style={{ wordBreak: "break-word" }}>
                {applicant.worker.description}
              </Text>
            </div>
          </ProfileDetailsContainer>

          <VideoContainer>
            <LabelStyled>{t("ProfileWorker.VideoPresentation")}</LabelStyled>

            {workerVideo ? (
              <VideoWrapper>
                <VideoPresentation controls src={workerVideo} />
              </VideoWrapper>
            ) : (
              <Text color={Color.SeaBlue400}>
                {t("ProfileWorker.NoVideoPresentation")}
              </Text>
            )}
          </VideoContainer>
          <SkillsContainer>
            <LabelStyled>{t("ProfileWorker.Skills")}</LabelStyled>
            <SkillsCell>
              {applicant.worker.skills.map((skill, index) => (
                <SkillCell
                  onClick={() => {
                    workerEndorsements.find(
                      (endorsement) =>
                        endorsement.companyId === companyState.company?.id &&
                        endorsement.skill.toLowerCase() ===
                          skill.skill.toLowerCase(),
                    )
                      ? setSkillAlreadyEndorsed(true)
                      : setSkillAlreadyEndorsed(false);
                    setWorkerEndorsementModalOpen(true);
                    setCreateWorkerEndorsementSkill(skill);
                  }}
                >
                  <Column>
                    <Text
                      fontSize={FontSize.Large}
                      color={Color.MidnightBlue}
                      fontFamily={FontFamily.MontserratSemiBold}
                      style={{ marginBottom: "4px" }}
                    >
                      {t(skill.skill, { ns: "skills" })}
                    </Text>
                    <Text fontSize={FontSize.Small} color={Color.SeaBlue500}>
                      {skill.experience === 0
                        ? `3-9 ${t("ProfileWorker.Months")}`
                        : skill.experience < 2
                          ? `1-2 ${t("ProfileWorker.Years")}`
                          : skill.experience < 5
                            ? `2-5 ${t("ProfileWorker.Years")}`
                            : `+5 ${t("ProfileWorker.Years")}`}
                    </Text>
                  </Column>
                  <Text
                    fontSize={FontSize.Small}
                    color={Color.SeaBlue500}
                    fontFamily={FontFamily.MontserratRegular}
                  >
                    {
                      workerEndorsements.filter(
                        (endorsement) =>
                          endorsement.skill.toLowerCase() ===
                          skill.skill.toLowerCase(),
                      ).length
                    }{" "}
                    {t("ProfileWorker.Endorsements")}
                  </Text>
                </SkillCell>
              ))}
            </SkillsCell>
          </SkillsContainer>
          <WorkerDocuments documents={applicant.worker.documents} />
        </ProfileDiv>
        <DividerLine />
        <AboutDiv>
          {isPublicShift && (
            <GreenCell>
              <LabelStyled color={Color.MidnightBlue}>
                {t("ProfileWorker.RequestedRate")}
              </LabelStyled>
              <Text>
                {`${applicant.requestedRate} ${t("General.Currency")}`}
              </Text>
            </GreenCell>
          )}
          <Cell>
            <LabelStyled>{t("ProfileWorker.MotivationalLetter")}</LabelStyled>
            <Text>{applicant.motivationalLetter}</Text>
          </Cell>
          {applicant.declineReason ? (
            <Cell style={{ marginTop: 20 }}>
              <LabelStyled>{t("GigsCompany.NoteToApplicant")}</LabelStyled>
              <Text>{applicant.declineReason}</Text>
            </Cell>
          ) : null}
          {applicant.status === ApplicationStatus.PENDING && (
            <RowWrapper>
              <ButtonStyled
                backgroundColor={Color.Destructive}
                onClick={handleRejectApplicant}
              >
                {t("GigsCompany.Reject")}
              </ButtonStyled>
              <ButtonStyled
                backgroundColor={Color.BurntSienna}
                onClick={handleAcceptApplicant}
              >
                {t("ApplicationsCompany.Assign")}
              </ButtonStyled>
            </RowWrapper>
          )}
          {applicant.resolution === ApplicationResolution.ACCEPTED && (
            <RowWrapper>
              <Text
                backgroundColor={Color.SeaBlue400}
                padding
                color={Color.White}
              >
                {t("ApplicationsCompany.Hired")}
              </Text>
            </RowWrapper>
          )}
          {applicant.status === ApplicationStatus.OFFERED && (
            <RowWrapper>
              <Text
                backgroundColor={Color.SeaBlue400}
                padding
                color={Color.White}
              >
                {t("GigsCompany.Offered")}
              </Text>
            </RowWrapper>
          )}
          {applicant.resolution === ApplicationResolution.REJECTED && (
            <RowWrapper>
              <Text
                backgroundColor={Color.Destructive}
                padding
                color={Color.White}
              >
                {t("Alerts.Rejected")}
              </Text>
            </RowWrapper>
          )}
        </AboutDiv>
      </ApplicantGrid>
      {createWorkerEndorsementSkill && (
        <WorkerEndorementModal
          workerEndorsementModalOpen={workerEndorsementModalOpen}
          createWorkerEndorsementSkill={createWorkerEndorsementSkill}
          deleteEndorsement={deleteEndorsement}
          createEndorsement={createEndorsement}
          setWorkerEndorsementModalOpen={setWorkerEndorsementModalOpen}
          skillAlreadyEndorsed={skillAlreadyEndorsed}
          workerEndorsements={workerEndorsements}
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeResponseModal}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <ModalBody>{renderApplicationModalBody()}</ModalBody>
      </Modal>
      <Modal
        open={favoriteModalOpen}
        onClose={() => {
          setFavoriteModalOpen(false);
        }}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <ModalBody>{renderFavoriteModalBody()}</ModalBody>
      </Modal>
    </>
  );
};
